import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, Activity, HeartPulse, Zap, Radio, Shield, GraduationCap, MapPin } from "lucide-react";
import { MaskReveal, FadeUp } from "../components/Reveal";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { AwardGallery } from "../components/AwardGallery";
import { Testimonials } from "../components/Testimonials";
import { GodhraHospital } from "../components/GodhraHospital";
import { VideoPlayer } from "../components/VideoPlayer";
import { ContactSection } from "../components/ContactSection";
import { DOCTOR, IMAGES, SERVICES, PUBLICATIONS } from "../data/content";
import { useLang } from "../i18n/i18n";

const ICONS = { activity: Activity, "heart-pulse": HeartPulse, zap: Zap, radio: Radio, shield: Shield };

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const bgNumY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative min-h-screen overflow-hidden bg-ink px-6 pt-40 md:px-12 md:pt-44"
    >
      <div className="pointer-events-none absolute right-0 top-1/4 h-[600px] w-[600px] rounded-full bg-bluecore/25 blur-[150px]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[400px] w-[400px] rounded-full bg-crimson/15 blur-[140px]" />

      <motion.span
        style={{ y: bgNumY }}
        className="pointer-events-none absolute -right-6 top-40 select-none font-serif text-[26vw] leading-none text-stroke md:top-24"
        aria-hidden="true"
      >
        Heart
      </motion.span>

      <div className="relative mx-auto grid max-w-[1500px] items-center gap-12 pb-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">
        <motion.div style={{ y: textY }} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-crimson" />
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">
              {t.hero.kicker}
            </span>
          </motion.div>

          <h1 className="font-serif text-[13vw] font-light leading-[0.9] tracking-tight text-white sm:text-7xl md:text-8xl xl:text-9xl">
            <MaskReveal lines={[t.hero.l1, t.hero.l2]} delay={0.15} />
            <span className="mt-2 block italic text-crimson">
              <MaskReveal lines={[t.hero.l3]} delay={0.4} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 max-w-xl text-lg leading-relaxed text-white/60"
          >
            {t.hero.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/services"
              data-testid="hero-explore-button"
              className="group relative overflow-hidden border border-crimson bg-crimson px-8 py-4 text-sm uppercase tracking-[0.15em] text-white"
            >
              <span className="relative z-10 flex items-center gap-2">
                {t.hero.cta1}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <span className="absolute inset-0 -translate-x-full bg-ink transition-transform duration-400 group-hover:translate-x-0" />
            </Link>
            <a
              href={DOCTOR.phoneHref}
              data-testid="hero-call-button"
              className="group flex items-center gap-2 border border-white/20 px-8 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-white/60"
            >
              <Phone className="h-4 w-4 text-crimson" />
              {t.hero.cta2}
            </a>
          </motion.div>
        </motion.div>

        <div className="relative flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-crimson/30 sm:-inset-4" />
            <motion.div
              style={{ y: portraitY }}
              className="clip-corner relative h-[440px] w-full max-w-[340px] overflow-hidden bg-ink2 sm:h-[520px] md:h-[600px] md:max-w-[460px]"
            >
              <img src={IMAGES.heroPortrait} alt={DOCTOR.name} loading="eager" fetchpriority="high" decoding="async" className="h-full w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            </motion.div>
            <div className="absolute -bottom-5 -left-3 z-10 border border-white/10 bg-ink/90 px-5 py-3 backdrop-blur-md sm:-bottom-6 sm:-left-6 sm:px-6 sm:py-4">
              <p className="font-serif text-xl text-white sm:text-2xl">MBBS · MD · DM</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">Cardiology</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{t.hero.scroll}</span>
        <span className="h-12 w-px bg-gradient-to-b from-crimson to-transparent" />
      </motion.div>
    </section>
  );
}

function StatsBand() {
  const { t } = useLang();
  return (
    <section data-testid="stats-band" className="border-y border-white/10 bg-ink2">
      <div className="mx-auto grid max-w-[1500px] grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
        {t.stats.map((s, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div className="px-6 py-12 text-center md:py-16">
              <p className="font-serif text-5xl font-light text-white md:text-6xl">{s.value}</p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{s.label}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function AboutPreview() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <section data-testid="about-preview" className="relative overflow-hidden bg-ink2 px-6 py-28 md:px-12 md:py-36">
      <div className="mx-auto grid max-w-[1500px] items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
        <FadeUp>
          <div ref={ref} className="relative mx-auto w-full max-w-[420px]">
            <div className="absolute -inset-3 border border-crimson/25" />
            <div className="clip-corner relative h-[440px] w-full overflow-hidden bg-ink sm:h-[560px]">
              <motion.img style={{ y: imgY }} src={IMAGES.aboutPortrait} alt={DOCTOR.name} loading="lazy" decoding="async" className="h-[115%] w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink2/70 via-transparent to-transparent" />
            </div>
          </div>
        </FadeUp>

        <div>
          <FadeUp>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.about.kicker}</span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white md:text-6xl">{t.about.title}</h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/70">
                <GraduationCap className="h-4 w-4 text-crimson" />
                {t.about.role}
              </span>
              <span className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/70">
                <MapPin className="h-4 w-4 text-crimson" />
                {t.about.city}
              </span>
            </div>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="mt-8 max-w-xl leading-relaxed text-white/60">{t.about.body}</p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <Link
              to="/about"
              data-testid="about-preview-link"
              className="group mt-10 inline-flex items-center gap-2 border border-white/20 px-8 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors hover:border-crimson hover:text-crimson"
            >
              {t.about.readMore}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { t } = useLang();
  return (
    <section data-testid="services-preview" className="relative bg-ink px-6 py-28 md:px-12 md:py-36">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-16 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.services.kicker}</span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white md:text-6xl">
              {t.services.title1}
              <br />
              {t.services.title2}
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-preview-viewall"
            className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-white/70 transition-colors hover:text-crimson"
          >
            {t.services.viewAll}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Activity;
            const item = t.services.items[i];
            return (
              <FadeUp key={s.id} delay={i * 0.08}>
                <div className="group relative h-full border border-white/10 bg-ink2 p-8 transition-colors duration-400 hover:border-crimson/60">
                  <div className="mb-8 flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center border border-crimson/40 text-crimson transition-colors duration-400 group-hover:bg-crimson group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-xs text-white/25">{s.id}</span>
                  </div>
                  <h3 className="font-serif text-2xl leading-snug text-white">{item.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-white/50">{item.body}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ResearchPreview() {
  const { t } = useLang();
  return (
    <section data-testid="research-preview" className="relative overflow-hidden bg-ink2 px-6 py-28 md:px-12 md:py-36">
      <div
        className="pointer-events-none absolute inset-0 opacity-15"
        style={{ backgroundImage: `url(${IMAGES.lab})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="pointer-events-none absolute inset-0 bg-ink2/70" />
      <div className="relative mx-auto max-w-[1500px]">
        <FadeUp>
          <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.research.kicker}</span>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight text-white md:text-6xl">{t.research.title}</h2>
        </FadeUp>

        <div className="mt-16 divide-y divide-white/10 border-y border-white/10">
          {PUBLICATIONS.slice(0, 3).map((p, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <div className="group flex flex-col gap-3 py-7 transition-colors hover:bg-white/[0.02] md:flex-row md:items-center md:gap-8">
                <span className="font-mono text-xs text-crimson md:w-16">0{i + 1}</span>
                <p className="flex-1 text-lg text-white/85 md:text-xl">{p.title}</p>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/40">{p.journal} {p.year}</span>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <Link
            to="/research"
            data-testid="research-preview-viewall"
            className="group mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-white transition-colors hover:text-crimson"
          >
            {t.research.viewAll}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

function MediaTeaser() {
  const { t } = useLang();
  return (
    <section data-testid="media-teaser" className="relative overflow-hidden bg-ink px-6 py-28 md:px-12 md:py-36">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[400px] w-[400px] rounded-full bg-bluecore/20 blur-[150px]" />
      <div className="relative mx-auto max-w-[1300px]">
        <FadeUp>
          <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.media.kicker}</span>
              <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white md:text-6xl">{t.media.title}</h2>
            </div>
            <Link
              to="/media"
              data-testid="media-teaser-link"
              className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-white/70 transition-colors hover:text-crimson"
            >
              {t.media.link}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <VideoPlayer poster={IMAGES.orRoom} />
        </FadeUp>
      </div>
    </section>
  );
}

function ContactHome() {
  return (
    <section data-testid="home-contact" className="border-t border-white/10 pt-24 md:pt-32">
      <ContactSection withHeading />
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <StatsBand />
      <AboutPreview />
      <GodhraHospital />
      <AwardGallery />
      <ServicesPreview />
      <Testimonials />
      <ResearchPreview />
      <MediaTeaser />
      <ContactHome />
    </div>
  );
}
